'use client';

import { c } from '../../../utils/tailwind-merge';

import { CheckCircle, Grid, HexagonWithCheck, Loader } from 'phosphor-icons';
import { Popover } from '../../atoms/Popover/Popover';
import { PublisherInfo } from '../../molecules/PublisherInfo/PublisherInfo';
import { ReviewStars } from '../../molecules/ReviewStars/ReviewStars';
import { RichText } from '../../molecules/RichText/RichText';
import { ProductPricing } from '../ProductPricing';
import {
  ProductInfoProps,
  ProductInfoPropsMicrocopy,
} from './ProductInfo.types';

const renderVerifiedSolutionsTooltip = (
  content: NonNullable<
    ProductInfoPropsMicrocopy['verifiedSolutions']
  >['tooltip']
): React.ReactNode => {
  const { title, benefit1, benefit2, benefit3, cta } = content;
  const flexItemBadge = 'flex items-center';
  return (
    <>
      {title && (
        <RichText
          html={title}
          className="text-sm [&_a]:text-sm [&_a]:no-underline [&_span]:mb-0 [&_span]:text-grey-700"
        />
      )}
      {benefit1 && (
        <div className="px-3 py-1 pb-0">
          <div className={flexItemBadge}>
            <CheckCircle
              className="mr-3 inline-block text-blue"
              size={30}
              aria-hidden="true"
            />
            <RichText
              html={benefit1}
              className="py-1.5 text-sm [&_a]:no-underline"
              type="p"
            />
          </div>
          {benefit2 && (
            <div className={flexItemBadge}>
              <div className="mr-1 pt-1">
                <Loader aria-hidden="true" />
              </div>
              <RichText html={benefit2} className="py-1.5 text-sm" />
            </div>
          )}
          {benefit3 && (
            <div className={flexItemBadge}>
              <div className="mr-1 pt-1">
                <Grid aria-hidden="true" />
              </div>
              <RichText html={benefit3} className="py-1.5 text-sm" />
            </div>
          )}
        </div>
      )}
      {cta && (
        <div className="mt-1 py-1">
          <RichText
            html={cta}
            className="py-1.5 text-sm [&_a]:no-underline [&_p]:mb-0"
            type="p"
          />
        </div>
      )}
    </>
  );
};

const renderVerifiedSolutionsBadge = (
  microcopy: NonNullable<ProductInfoPropsMicrocopy['verifiedSolutions']>
): React.ReactNode => (
  <Popover>
    <Popover.Trigger
      data-test="product-info-verified-solutions-badge"
      aria-label={microcopy.text}
      className="flex min-h-6 w-fit cursor-pointer items-start gap-1 border-none bg-transparent p-0 text-sm font-medium text-blue"
    >
      <HexagonWithCheck
        size={20}
        color="currentColor"
        className="shrink-0"
        aria-hidden="true"
      />
      {microcopy.text}
    </Popover.Trigger>
    <Popover.Content className="z-50">
      {renderVerifiedSolutionsTooltip(microcopy.tooltip)}
    </Popover.Content>
  </Popover>
);

export const ProductInfo = ({
  variant = 'default',
  isVerifiedSolution = false,
  productTitle,
  publisherName,
  publisherLink,
  isCertifiedPublisher,
  publisherAccountType,
  publisherAvatarUrl,
  price,
  sale,
  entitlement,
  userType,
  microcopy,
  className = '',
  customEula,
  reviews,
  isFavorite,
  isReviewsEnabled = false,
  isPurchasing = false,
  isClaiming = false,
  isAddToCartEnabled,
  isInCart,
  entitlementKind,
  quantity,
  onQuantityChange,
  isSeatAssigned,
  unassignedSeatCount,
  hasSeatData,
  onAddToMyAssets,
  onBuyNow,
  onAddToCart,
  onViewInCart,
  onRequestSeat,
  onOpenInUnity,
  onFavorite,
  onManageSeats,
}: ProductInfoProps) => {
  const addToCartProps =
    onAddToCart && onViewInCart
      ? { isAddToCartEnabled, onAddToCart, onViewInCart }
      : { isAddToCartEnabled: false as const };

  return variant === 'default' ? (
    <div
      data-test={'product-info-section'}
      className={c('flex flex-col gap-y-4', className)}
    >
      {isVerifiedSolution &&
        microcopy.verifiedSolutions &&
        renderVerifiedSolutionsBadge(microcopy.verifiedSolutions)}
      <h2
        data-test="product-title"
        className="text-xl font-semibold leading-8 text-grey-900"
      >
        {productTitle}
      </h2>

      {isReviewsEnabled && reviews && microcopy.reviewStars && (
        <ReviewStars
          value={reviews.value}
          max={reviews.max}
          reviews={reviews.reviews}
          microcopy={microcopy.reviewStars}
          variant="small"
          showReviewsLink={true}
        />
      )}

      <PublisherInfo
        showAvatar={true}
        isCertified={isCertifiedPublisher}
        publisherAccountType={publisherAccountType}
        publisherName={publisherName}
        publisherLink={publisherLink}
        publisherAvatarUrl={publisherAvatarUrl}
        microcopy={{
          certifiedPublisher: microcopy.certifiedPublisher,
          verifiedOrganization: microcopy.verifiedOrganization,
          verifiedIndividual: microcopy.verifiedIndividual,
        }}
      />

      <ProductPricing
        variant="default"
        entitlement={entitlement}
        userType={userType}
        price={price}
        sale={sale}
        customEula={customEula}
        isFavorite={isFavorite}
        isPurchasing={isPurchasing}
        isClaiming={isClaiming}
        {...addToCartProps}
        isInCart={isInCart}
        entitlementKind={entitlementKind}
        quantity={quantity}
        onQuantityChange={onQuantityChange}
        isSeatAssigned={isSeatAssigned}
        unassignedSeatCount={unassignedSeatCount}
        hasSeatData={hasSeatData}
        microcopy={microcopy}
        onAddToMyAssets={onAddToMyAssets}
        onBuyNow={onBuyNow}
        onRequestSeat={onRequestSeat}
        onOpenInUnity={onOpenInUnity}
        onFavorite={onFavorite}
        onManageSeats={onManageSeats}
      />
    </div>
  ) : (
    <div
      className={c(
        'fixed bottom-0 left-0 right-0 z-50 border-t border-grey-300 bg-grey-50 lg:fixed lg:bottom-auto lg:top-0 lg:border-b lg:border-t-0',
        className
      )}
    >
      <div className="lg-container flex w-full items-start justify-between py-3">
        <div className="hidden flex-col lg:flex">
          <h3 className="text-gray-900 text-md font-medium leading-7">
            {productTitle}
          </h3>
          <div className="flex items-center">
            <PublisherInfo
              size="small"
              showAvatar={false}
              isCertified={isCertifiedPublisher}
              publisherAccountType={publisherAccountType}
              publisherName={publisherName}
              publisherLink={publisherLink}
              publisherAvatarUrl={publisherAvatarUrl}
              microcopy={{
                certifiedPublisher: microcopy.certifiedPublisher,
                verifiedOrganization: microcopy.verifiedOrganization,
                verifiedIndividual: microcopy.verifiedIndividual,
              }}
            />
          </div>
        </div>

        <ProductPricing
          variant="sticky"
          entitlement={entitlement}
          userType={userType}
          price={price}
          sale={sale}
          customEula={customEula}
          isFavorite={isFavorite}
          isPurchasing={isPurchasing}
          isClaiming={isClaiming}
          {...addToCartProps}
          isInCart={isInCart}
          entitlementKind={entitlementKind}
          quantity={quantity}
          onQuantityChange={onQuantityChange}
          isSeatAssigned={isSeatAssigned}
          unassignedSeatCount={unassignedSeatCount}
          hasSeatData={hasSeatData}
          microcopy={microcopy}
          onAddToMyAssets={onAddToMyAssets}
          onBuyNow={onBuyNow}
          onRequestSeat={onRequestSeat}
          onOpenInUnity={onOpenInUnity}
          onFavorite={onFavorite}
          onManageSeats={onManageSeats}
        />
      </div>
    </div>
  );
};
